import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  useTheme,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { authAPI } from '../apis';
import { User } from '../types';

const SettingsContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
}));

const SettingsSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SettingsSkeleton = () => {
  const theme = useTheme();
  return (
    <Box sx={{ pt: '84px', minHeight: '100vh', bgcolor: theme.palette.background.default, pb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={200} height={48} />
      </Box>

      <SettingsContainer>
        <SettingsSection>
          <Skeleton variant="text" width={240} height={32} sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            {[...Array(5)].map((_, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        </SettingsSection>

        <Skeleton variant="text" sx={{ my: 3 }} />

        <SettingsSection>
          <Skeleton variant="text" width={280} height={32} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[...Array(3)].map((_, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Skeleton variant="rectangular" width={58} height={38} sx={{ borderRadius: 24 }} />
                  <Skeleton variant="text" width={160} sx={{ ml: 2 }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </SettingsSection>

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
        </Box>
      </SettingsContainer>
    </Box>
  );
};

const Settings = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await authAPI.getUser();
        if (response.status === 'success' && response.data.user) {
          setUser(response.data.user);
        } else {
          setError('Failed to load user data');
        }
      } catch (err) {
        setError('An error occurred while fetching user data');
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleInputChange = (field: keyof User) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (user) {
      setUser({
        ...user,
        [field]: event.target.value
      });
    }
  };

  const handleNotificationChange = (type: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (user) {
      const currentPreferences = user.notificationPreferences || [];
      let newPreferences;
      
      if (event.target.checked) {
        newPreferences = [...currentPreferences, type];
      } else {
        newPreferences = currentPreferences.filter(pref => pref !== type);
      }

      setUser({
        ...user,
        notificationPreferences: newPreferences
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Add updateUser endpoint to authAPI
      // await authAPI.updateUser(user);
      setSuccess('Settings updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update settings. Please try again.');
      console.error('Error updating user settings:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <SettingsSkeleton />;
  }

  return (
    <Box sx={{ pt: '84px', minHeight: '100vh', bgcolor: theme.palette.background.default, pb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <SettingsContainer>
        <SettingsSection>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
            Personal Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={user?.username || ''}
                onChange={handleInputChange('username')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={user?.email || ''}
                onChange={handleInputChange('email')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={user?.phone || ''}
                onChange={handleInputChange('phone')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Organization"
                value={user?.organization || ''}
                onChange={handleInputChange('organization')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Preferred Language"
                value={user?.preferredLanguage || ''}
                onChange={handleInputChange('preferredLanguage')}
              />
            </Grid>
          </Grid>
        </SettingsSection>

        <Divider sx={{ my: 3 }} />

        <SettingsSection>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
            Notification Preferences
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={user?.notificationPreferences?.includes('email') || false}
                    onChange={handleNotificationChange('email')}
                  />
                }
                label="Email Notifications"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={user?.notificationPreferences?.includes('push') || false}
                    onChange={handleNotificationChange('push')}
                  />
                }
                label="Push Notifications"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={user?.notificationPreferences?.includes('sms') || false}
                    onChange={handleNotificationChange('sms')}
                  />
                }
                label="SMS Notifications"
              />
            </Grid>
          </Grid>
        </SettingsSection>

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              bgcolor: '#1E4C9A',
              '&:hover': {
                bgcolor: '#153a75',
              },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </SettingsContainer>
    </Box>
  );
};

export default Settings; 